import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy, useMemo, type ComponentType } from "react";
import { ScrollRegion } from "@offsilo/ui/scroll";
import { usePageChrome } from "@/app/chrome";

// Each component family may ship packages/ui/src/<family>/lab.tsx with a default export showing every variant and
// state. This route finds them by glob, so adding a family never edits a shared file. Dev and review only.
const labs = import.meta.glob<{ default: ComponentType }>("../../../../../../packages/ui/src/*/lab.tsx");
const families = Object.keys(labs).map((p) => p.split("/").at(-2)!).sort();

export const Route = createFileRoute("/_app/lab/$family")({ component: Lab });

function Lab() {
  const { family } = Route.useParams();
  usePageChrome([{ id: "lab", label: "Lab", href: "/lab/index" }, { id: family, label: family }]);
  const key = Object.keys(labs).find((p) => p.split("/").at(-2) === family);
  const Page = useMemo(() => (key ? lazy(labs[key]!) : null), [key]);
  return (
    <ScrollRegion name="lab" gutter>
      <div className="flex min-h-full">
        <nav aria-label="Lab families" className="w-48 shrink-0 border-e border-row-line p-3 max-tablet:hidden">
          {families.map((f) => (
            <Link key={f} to="/lab/$family" params={{ family: f }} className="block rounded-md px-2 py-1.5 text-sm text-fg-muted hover:bg-hover data-[status=active]:bg-sel data-[status=active]:text-fg">
              {f}
            </Link>
          ))}
        </nav>
        <div className="min-w-0 flex-1 p-7 max-tablet:p-4">
          {Page ? <Suspense fallback={null}><Page /></Suspense> : <p className="text-fg-muted">Pick a family. {families.length} have lab pages.</p>}
        </div>
      </div>
    </ScrollRegion>
  );
}
